/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: PCG Viewer handoff-package import (the report's input contract).
// Source: cardiac_report_v0_5_6_dev_2.html lines 2648-2772 (applyPcgFeature),
// 2832-2851 (buildSysDiaDurationEvidence), 2853-3006 (applyPcgViewerHandoff,
// pure mapping only — the fetch('/handoff/pcg/latest') + localStorage fallback +
// import-status persistence are platform plumbing and are NOT vendored),
// 3020-3046 (ensureSysDiaDurationFeature), 3048-3152 (normalizeCandidateReportStructure),
// 4982-5004 (dedupeFeatureSources).
// Deviations: data globals -> explicit `report` model argument; Date formatting
// injectable via options for determinism; console noise dropped; the derived
// SYS:DIA Duration evidence inherits the cardiac-cycle block's review flag.
// Heart Report v0.5.8: the Heart Rate card carries the accepted range
// (`sourceRangeBpm`, shown as two markers), and when the roll-up flagged the
// accepted Timing recordings as disagreeing the four timing cards arrive
// pending, with the roll-up's account on the Heart Rate card's
// `candidateEvidenceMeta`, until the reviewer decides. Each timing card also
// takes the roll-up's per-recording records (`timingSourceRecordings`, all
// ticked) and derives its value from them (see timing-sources.js), so the
// reviewer can choose which accepted recordings each card includes.
// Heart Report v0.5.8 at cardiac-panel-v4 git 94432d0 — importedEvidenceUnavailable,
// featureRequiresAnalyticalMeasurement, setFeatureUnassessed and the
// accepted / candidate / nothing branches of the import:
// - Evidence that measured nothing ("Not assessed", a grey or unavailable
//   status) never gives a card a result or an abnormal status. The card is
//   reset to a neutral, unassessed state for a manual assessment — or, for
//   the SYS:DIA Ratio and SYS:DIA Duration cards, which state a calculated
//   measurement, left out of the report as "measurement unavailable".
// - When nothing was accepted for a domain but the roll-up carries candidate
//   evidence for it (`pkg.candidateEvidence`), the cards take the candidate's
//   values and arrive pending: derived evidence to review and Save, edit, or
//   Exclude. It is never treated as accepted.
// Deviations: an unassessed card keeps a PCG Viewer source line carrying the
// roll-up's reason (upstream leaves an earlier import's source in place, or
// drops it on the Structural Sound card), because a report here is re-derived
// when the review changes and its Evidence panel must not describe evidence
// that is gone; a candidate's `candidateEvidenceMeta` is a short record (its
// domain, source recordings and review reason), not the whole candidate block;
// Structural Sound detail is attached only beside a summary that has a result.
// Medaica Report v0.6.1 (cardiac-panel-v4 git 6f8684d):
// - A heart rate the HCP entered in the viewer is the accepted rate. The
//   roll-up substitutes it per recording before it gets here, so the Heart
//   Rate card's value, range and marker follow the substituted rates as for
//   any import; what the card adds is its account — the PCG Viewer source
//   line says the BPM was entered in the viewer, with the automated estimate
//   it replaced and the reviewer's note — and `acceptedSource`, so the line
//   survives a source toggle (see timing-sources.js). Whether a value is
//   present is a real null check: `Number(null)` is 0, so coercion would read
//   the viewer's default `hcpConfirmedBpm: null` as a confirmed rate.
//   Deviation: upstream collapses the exam to the one value and skips the
//   per-recording recalculation; a toggle there then drops the confirmed
//   value without a trace.
// - The package's supporting-context blocks (`pkg.context`) annotate the
//   Signal Quality card's PCG Viewer source line only: the evidence policy
//   sentence and the questionnaire count. The capture-time BPM comparison
//   belongs to Heart Rate and fires only when the package carries a live BPM.

import {
  PATIENT_SUMMARY_TEXT,
  DEFAULT_RECORDING_NOTE_INSTRUCTION,
  HANDOFF_SCHEMA,
  rhythmRegularityReferenceText,
} from "./constants"
import { parseLeadingNumber, parseLegacyRegularityPct, parseSysDiaMsFromEvidence } from "./parse"
import { expectedDiaRangeForHeartRate } from "./zones"
import {
  findFeatureByName,
  currentHeartRateBpm,
  heartRateRangeOf,
  isCardiacCycleTimingFeature,
  isHeartRateFeature,
  isSysDiaDurationFeature,
  isRhythmRegularityFeature,
  sourceIdForFeed,
  buildFeatureBaseline,
} from "./features"
import {
  rhythmRegularityObservedText,
  rhythmRegularityPatternLabel,
  rhythmRegularityColorClass,
} from "./timing-pattern"
import { recordingNoteObservationText } from "./review"
import { attachThirdPartySources, removePlaceholderThirdPartySources } from "./third-party-sources"
import {
  getColorClass,
  cardiacCycleTimingColorClass,
  heartRateRangeGrades,
  sysDiaDurationColorClass,
  sysDiaDurationStatusFromValues,
  structuralSoundResultLabel,
} from "./status"
import {
  calculateMarkerPosition,
  formatSystoleDiastoleDisplay,
  heartRateRangeMarkerPosition,
  normalizeCardiacCycleEvidenceFinding,
} from "./marker"
import {
  setAutoPatientCopy,
  getAutoProviderNote,
  composePatientNote,
  sanitizeProviderFollowUpText,
  providerSummaryText,
} from "./copy"
import { displayReferenceRange } from "./display"
import {
  INCLUSION_ACCEPTED,
  INCLUSION_EXCLUDED,
  INCLUSION_PENDING,
  INCLUSION_UNRESOLVED,
  MEASUREMENT_UNAVAILABLE_ORIGIN,
  MEASUREMENT_UNAVAILABLE_REASON,
  TIMING_DOMAIN_CARDS,
  markReportEvidenceState,
  reconcileReportInclusionStates,
} from "./inclusion"
import {
  HCP_ENTERED_VIEWER_SOURCE,
  automatedHeartRateSourceOf,
  durationStatisticOf,
  heartRateConfirmedInViewer,
  heartRateSourceFinding,
  recalculateTimingFeatureFromSources,
} from "./timing-sources"
import { structuralSourceRecordingsFrom } from "./structural-sources"

// A number the package actually carries; null, undefined, "" and NaN are all
// "none" (never coerced: Number(null) is 0)
const presentNumber = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) ? Number(value) : null

const contextBlock = (pkg, key) => (pkg && pkg.context && pkg.context[key]) || {}

/**
 * The supporting-context sentences a card's PCG Viewer source line carries:
 * on Signal Quality, that live exam data and questionnaire context never
 * override the acoustic measurements, and how many questionnaire responses
 * there are; on Heart Rate, the capture-time BPM beside the viewer's, when
 * the package has one.
 */
export function supportingContextNotes(pkg, featureName) {
  const notes = []
  if (featureName === "Signal Quality") {
    if (contextBlock(pkg, "contextEvidencePolicy").doesNotOverrideAcousticMeasurements) {
      notes.push(
        "Supporting context only: live exam data and questionnaire context do not override Viewer acoustic measurements."
      )
    }
    const patient = contextBlock(pkg, "patientContextEvidence")
    const responses = presentNumber(patient.questionnaireResponseCount)
    if (patient.available && responses !== null && responses > 0) {
      const flagged = presentNumber(patient.positiveResponseCount) || 0
      notes.push(
        `Patient context available: ${responses} questionnaire response${responses === 1 ? "" : "s"}${
          flagged ? `, ${flagged} flagged` : ""
        }.`
      )
    }
  }
  if (featureName === "Heart Rate") {
    const live = contextBlock(pkg, "provisionalLiveEvidence").bpm || {}
    const liveBpm = presentNumber(live.valueBpm)
    if (liveBpm !== null) {
      // Compared with the Evaluation Range's rate (upstream's wording) or,
      // for a guide without one, the rate over the selected usable cycles
      const evalRangeBpm = presentNumber(live.viewerEvalRangeBpm)
      const viewerBpm = evalRangeBpm === null ? presentNumber(live.viewerBpm ?? live.viewerCuratedCycleBpm) : null
      const delta = presentNumber(live.deltaBpm)
      const parts = [`Live/provisional BPM ${Math.round(liveBpm)} bpm`]
      if (evalRangeBpm !== null) parts.push(`Eval Range BPM ${Math.round(evalRangeBpm)} bpm`)
      if (viewerBpm !== null) parts.push(`Viewer BPM ${Math.round(viewerBpm)} bpm`)
      if (delta !== null) parts.push(`delta ${Math.round(delta)} bpm`)
      if (live.halfDoubleRelation) parts.push("possible half/double relationship")
      notes.push(`${parts.join("; ")}. This is discrepancy context only.`)
    }
  }
  return notes
}

// The evidence block with the card's supporting-context sentences attached
const withSupportingContext = (pkg, featureName, incoming) =>
  incoming ? { ...incoming, supportingContextNotes: supportingContextNotes(pkg, featureName) } : incoming

const UNAVAILABLE_STATUSES = new Set(["grey", "gray", "unavailable", "unassessable", "not_assessed"])
const UNAVAILABLE_OBSERVED = new Set(["not assessed", "unassessable", "unavailable"])
const text = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()

/** Whether an evidence block measured nothing: absent, grey / unavailable, or "Not assessed". */
export function importedEvidenceUnavailable(incoming) {
  if (!incoming) return true
  return (
    UNAVAILABLE_STATUSES.has(text(incoming.status || incoming.state)) ||
    UNAVAILABLE_OBSERVED.has(text(incoming.observed)) ||
    /^(unassessable|unavailable|not_assessed)/.test(text(incoming.detailStatus || incoming.phaseStatus))
  )
}

/** The cards that state a calculated measurement and cannot be assessed by hand. */
export function featureRequiresAnalyticalMeasurement(featureName) {
  return isCardiacCycleTimingFeature(featureName) || isSysDiaDurationFeature(featureName)
}

const isStructuralSoundFeature = (feature) =>
  feature.name === "Structural Sound" || feature.name === "Structural Sound Summary"

const NO_EVIDENCE_SOURCE_FINDING = "No accepted PCG Viewer evidence is available for this card."

/**
 * Resets a card that no evidence gave a result to: "Not assessed", grey, a
 * neutral marker, no recordings, ranges, pattern or notes. It waits in
 * clinical review for a manual assessment — unless it is a card that states
 * a calculated measurement, which is left out of the report until evidence
 * supplies one. `incoming` is the evidence block that measured nothing, when
 * there is one: its finding becomes the card's PCG Viewer source line.
 */
export function setFeatureUnassessed(report, featureName, incoming = null) {
  const feature = findFeatureByName(report.features, featureName)
  if (!feature) return false
  const measurementUnavailable = featureRequiresAnalyticalMeasurement(feature.name)
  feature.observed = "Not assessed"
  feature.status = "grey"
  feature.aiStatus = "not_assessed"
  feature.markerPosition = 50
  feature.timingSourceRecordings = []
  // An unassessed Structural Sound card still lists its accepted recordings,
  // each with the reason it cannot support a result
  feature.structuralSourceRecordings = isStructuralSoundFeature(feature) ? structuralSourceRecordingsFrom(incoming) : []
  feature.sourceRecordingIds = null
  feature.sourceRangeBpm = null
  feature.clinicianRangeBpm = null
  feature.clinicianAssessmentMode = null
  feature.mixedRange = false
  feature.clinicianRatioRange = null
  feature.clinicianSysDiaDurationRange = null
  feature.representativeSdRatio = null
  feature.representativeSysMs = null
  feature.representativeDiaMs = null
  feature.reviewRequired = false
  feature.reviewReason = ""
  feature.reviewTouched = false
  feature.timingPatternLabel = ""
  feature.rhythmPattern = ""
  feature.regularityPct = null
  feature.cycleVariability = null
  feature.timingVariabilityPct = null
  feature.robustSpreadPct = null
  feature.timingSwingPct = null
  if (isStructuralSoundFeature(feature)) {
    feature.structuralSoundLabel = "Unable to assess"
    feature.sysCyclePct = null
    feature.diaCyclePct = null
    feature.maxCyclePct = null
    feature.dominantPhase = null
    feature.energyStrength = null
    feature.structuralDetailEvidence = null
  }
  feature.dataSource = "viewer"
  feature.noteToPatientEdited = false
  feature.noteToPatientStale = false
  feature.noteToDoctorEdited = !!feature.noteToDoctorEdited
  if (!feature.noteToDoctorEdited) feature.noteToDoctor = ""
  feature.noteToDoctorStale = false
  feature.measurementUnavailable = measurementUnavailable
  feature.measurementUnavailableReason = measurementUnavailable ? MEASUREMENT_UNAVAILABLE_REASON : ""
  feature.sources = (feature.sources || []).filter(
    (source) => sourceIdForFeed(source) !== "pcg_viewer" && source.name !== "PCG Viewer Detail"
  )
  feature.sources.unshift({
    sourceId: "pcg_viewer",
    sourceLabel: "PCG Viewer",
    sourceType: "medaica",
    name: "PCG Viewer",
    finding: incoming?.finding || NO_EVIDENCE_SOURCE_FINDING,
    recordingId: null,
    position: null,
    reviewStatus: "unreviewed",
  })
  markReportEvidenceState(feature, measurementUnavailable ? INCLUSION_EXCLUDED : INCLUSION_UNRESOLVED)
  feature.reportInclusionOrigin = measurementUnavailable ? MEASUREMENT_UNAVAILABLE_ORIGIN : null
  // Patient-facing copy says that there is no result, never a default one
  setAutoPatientCopy(feature, feature.status)
  feature.importedBaseline = buildFeatureBaseline(feature)
  return true
}

// What a pending card keeps of the candidate evidence it was filled from
const candidateMeta = (candidate, domain) => ({
  candidate: true,
  domain,
  sourceRecordingIds: Array.isArray(candidate.sourceRecordingIds) ? [...candidate.sourceRecordingIds] : [],
  reviewRequired: !!candidate.reviewRequired,
  reviewReason: candidate.reviewReason || "",
})

/**
 * Gives the four timing cards per-recording records — the roll-up's, one for
 * every recording accepted for Timing, or a candidate's — each ticked, and
 * derives each card's value from them, taking that as the card's imported
 * baseline. With `includedIds`, records naming a recording outside that set
 * are left out (unaccepted recordings are never offered beside accepted
 * ones). Without records the cards keep the values the evidence gave them.
 */
function applyTimingSourceRecordings(report, exported, includedIds = new Set()) {
  const records = (Array.isArray(exported) ? exported : [])
    .filter((source) => !includedIds.size || includedIds.has(String(source.recordingId)))
    .map((source) => ({ ...source, included: true }))
  TIMING_DOMAIN_CARDS.forEach((name) => {
    const feature = findFeatureByName(report.features, name)
    if (!feature) return
    feature.timingSourceRecordings = records.map((source) => ({ ...source }))
    if (records.length) recalculateTimingFeatureFromSources(report.features, report.features.indexOf(feature), false)
    feature.importedBaseline = buildFeatureBaseline(feature)
  })
}

const finiteRange = (range) =>
  !!range &&
  range.min !== null &&
  range.max !== null &&
  Number.isFinite(Number(range.min)) &&
  Number.isFinite(Number(range.max))

// What the Heart Rate card keeps of the roll-up's account of a disagreement
// between the accepted Timing recordings, for the reviewer's decision
const timingRangeMeta = (heartRate) => ({
  timingRange: true,
  reviewReason: heartRate.reviewReason || "",
  rangeBpm: finiteRange(heartRate.rangeBpm) ? { ...heartRate.rangeBpm } : null,
  sourceRecordingIds: Array.isArray(heartRate.sourceRecordingIds) ? [...heartRate.sourceRecordingIds] : [],
  sourceRecordings: (heartRate.sourceRecordings || []).map((item) => ({ ...item })),
  proposedClusterRecordingIds: [...(heartRate.proposedClusterRecordingIds || [])],
  proposedClusterRangeBpm: heartRate.proposedClusterRangeBpm ? { ...heartRate.proposedClusterRangeBpm } : null,
  discrepantRecordings: (heartRate.discrepantRecordings || []).map((item) => ({ ...item })),
})

export function applyPcgFeature(report, featureName, incoming) {
  const features = report.features
  const feature = findFeatureByName(features, featureName)
  if (!feature || !incoming) return false
  // Evidence that measured nothing gives the card no result
  if (importedEvidenceUnavailable(incoming)) return setFeatureUnassessed(report, featureName, incoming)
  feature.measurementUnavailable = false
  feature.measurementUnavailableReason = ""
  if (feature.reportInclusionOrigin === MEASUREMENT_UNAVAILABLE_ORIGIN) feature.reportInclusionOrigin = null
  const importedNumber = (value) => {
    if (value === undefined || value === null || value === "") return null
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }
  if (feature.name === "Systolic/Diastolic Timing" || feature.name === "Cardiac Cycle Timing")
    feature.name = "SYS:DIA Ratio"
  if (incoming.observed !== undefined && incoming.observed !== null) feature.observed = incoming.observed
  if (incoming.status) feature.status = incoming.status
  if (incoming.normalRange) feature.normalRange = incoming.normalRange
  if (incoming.displayReferenceRange) feature.displayReferenceRange = incoming.displayReferenceRange
  if (isRhythmRegularityFeature(feature)) {
    const observedWithPattern = rhythmRegularityObservedText(incoming)
    if (observedWithPattern) feature.observed = observedWithPattern
    feature.timingPatternLabel = rhythmRegularityPatternLabel(
      {
        timingPatternLabel: incoming.timingPatternLabel,
        rhythmPattern: incoming.rhythmPattern,
        regularityPct: incoming.regularityPct,
        cycleVariability: incoming.cycleVariability,
        timingVariabilityPct: incoming.timingVariabilityPct ?? incoming.robustSpreadPct,
        timingSwingPct: incoming.timingSwingPct,
        expectedNormalVariabilityPct: incoming.expectedNormalVariabilityPct,
        expectedNormalSwingPct: incoming.expectedNormalSwingPct,
        irregularVariabilityPct: incoming.irregularVariabilityPct,
        irregularSwingPct: incoming.irregularSwingPct,
        selectedCycleCount: incoming.selectedCycleCount,
        rawMarkerConfidence: incoming.rawMarkerConfidence,
        timingCycleConfidence: incoming.timingCycleConfidence,
        markerConfidence: incoming.markerConfidence ?? incoming.confidence,
        observed: feature.observed,
      },
      incoming.status
    )
    feature.regularityPct = importedNumber(incoming.regularityPct) ?? parseLegacyRegularityPct(feature.observed)
    feature.cycleVariability = importedNumber(incoming.cycleVariability)
    feature.timingVariabilityPct = importedNumber(incoming.timingVariabilityPct ?? incoming.robustSpreadPct)
    feature.robustSpreadPct = feature.timingVariabilityPct
    feature.timingSwingPct = importedNumber(incoming.timingSwingPct)
    feature.robustSpreadMs = importedNumber(incoming.robustSpreadMs)
    feature.timingSwingMs = importedNumber(incoming.timingSwingMs)
    feature.medianCycleMs = importedNumber(incoming.medianCycleMs)
    feature.expectedNormalVariabilityPct = importedNumber(incoming.expectedNormalVariabilityPct)
    feature.expectedNormalSwingPct = importedNumber(incoming.expectedNormalSwingPct)
    feature.irregularVariabilityPct = importedNumber(incoming.irregularVariabilityPct)
    feature.irregularSwingPct = importedNumber(incoming.irregularSwingPct)
    feature.selectedCycleCount = importedNumber(incoming.selectedCycleCount)
    feature.rawMarkerConfidence = importedNumber(incoming.rawMarkerConfidence)
    feature.timingCycleConfidence = importedNumber(incoming.timingCycleConfidence)
    feature.markerConfidence = importedNumber(incoming.markerConfidence ?? incoming.confidence)
    feature.timingConfidence = incoming.timingConfidence || null
    feature.metricUsed = incoming.metricUsed || incoming.metric_used || null
    // Viewer review flags remain in the Viewer/audit package. They do not
    // become Report qualifications, approval gates, or wording.
    feature.confidenceRationaleCodes = []
    feature.confidenceRationaleText = ""
    feature.timingMethodFlags = []
    feature.highConfidenceMarkerPct = importedNumber(incoming.highConfidenceMarkerPct)
    feature.inferredMarkerPct = importedNumber(incoming.inferredMarkerPct)
    feature.s1s2GuideQuality = incoming.s1s2GuideQuality || null
    feature.s1s2OrientationScore = importedNumber(incoming.s1s2OrientationScore)
    feature.rateThresholdAdjusted = !!incoming.rateThresholdAdjusted
    feature.elevated25pctReference = !!incoming.elevated25pctReference
    feature.displayReferenceRange = rhythmRegularityReferenceText()
    feature.status = rhythmRegularityColorClass(feature, feature.status)
    feature.scaleType = "regularity"
  }
  if (isCardiacCycleTimingFeature(feature)) {
    feature.status = cardiacCycleTimingColorClass(feature.observed, feature.status, currentHeartRateBpm(features))
  }
  if (isSysDiaDurationFeature(feature)) {
    feature.status = sysDiaDurationColorClass(feature.observed, feature.status, currentHeartRateBpm(features))
  }
  if (feature.name === "Structural Sound" || feature.name === "Structural Sound Summary") {
    feature.structuralSoundLabel = structuralSoundResultLabel(
      { observed: incoming.observed || feature.observed },
      incoming.status || feature.status
    )
    feature.sysCyclePct = importedNumber(incoming.sysCyclePct)
    feature.diaCyclePct = importedNumber(incoming.diaCyclePct)
    feature.maxCyclePct = importedNumber(incoming.maxCyclePct)
    feature.dominantPhase = incoming.dominantPhase || null
    feature.energyStrength = incoming.energyStrength || null
    feature.phaseStatus = incoming.phaseStatus || null
    feature.unphasedEnergyDetected = !!incoming.unphasedEnergyDetected
    feature.structuralRule =
      incoming.structuralRule ||
      "Structural Sound status uses cycle repeatability only: <50% Normal, 50-75% Borderline, " +
        ">75% Elevated. Energy strength is supporting evidence."
    // Every recording accepted for Structural Sound, the result's one selected
    feature.structuralSourceRecordings = structuralSourceRecordingsFrom(incoming)
    feature.sourceRecordingId = incoming.sourceRecordingId || null
    feature.sourcePosition = incoming.sourcePosition || null
  }
  feature.status = getColorClass(
    feature.name,
    feature.observed,
    feature.normalRange,
    feature.status,
    feature.scaleType,
    currentHeartRateBpm(features)
  )
  feature.aiStatus = feature.status === "green" ? "normal" : feature.status === "red" ? "abnormal" : "borderline"
  feature.dataSource = "viewer"
  // Exam roll-up review flag: surfaced on the card, never an approval gate
  feature.reviewRequired = !!incoming.reviewRequired
  feature.reviewReason = incoming.reviewRequired ? incoming.reviewReason || "" : ""
  feature.sourceRecordingIds = Array.isArray(incoming.sourceRecordingIds) ? [...incoming.sourceRecordingIds] : null
  // An earlier import's account of its evidence does not describe this one;
  // the import-time state is marked afresh once every card is applied
  feature.candidateEvidenceMeta = null
  feature.approved = false
  feature.reviewTouched = false
  feature.sources = (feature.sources || []).filter((src) => src.name !== "PCG Viewer")
  // The source is named by its position only: the recording id is a
  // server identifier that means nothing to a reader (the prototype quotes
  // its file name as well)
  const sourceParts = []
  if (incoming.sourcePosition) sourceParts.push(`position: ${incoming.sourcePosition}`)
  const rhythmPatternNote =
    isRhythmRegularityFeature(feature) && feature.timingPatternLabel
      ? ` PCG timing pattern: ${feature.timingPatternLabel}.`
      : ""
  const structuralPatternNote =
    (feature.name === "Structural Sound" || feature.name === "Structural Sound Summary") && feature.structuralSoundLabel
      ? ` Structural Sound label: ${feature.structuralSoundLabel}.`
      : ""
  // Kept on the card: a Heart Rate card rewrites its source line when its
  // included recordings change (timing-sources.js) and must not drop them
  feature.supportingContextNotes = Array.isArray(incoming.supportingContextNotes)
    ? [...incoming.supportingContextNotes]
    : []
  const supportingContextNote = feature.supportingContextNotes.length
    ? ` ${feature.supportingContextNotes.join(" ")}`
    : ""
  if (isHeartRateFeature(feature)) {
    // How the accepted rate was arrived at, kept on the card for its source line
    feature.hcpConfirmedBpm = presentNumber(incoming.hcpConfirmedBpm)
    feature.acceptedSource =
      incoming.acceptedSource || (feature.hcpConfirmedBpm !== null ? HCP_ENTERED_VIEWER_SOURCE : null)
    feature.automatedSource = automatedHeartRateSourceOf(incoming)
    const automatedBpm = presentNumber(incoming.automatedValueBpm)
    feature.automatedObserved =
      incoming.automatedObserved || (automatedBpm !== null ? `${Math.round(automatedBpm)} bpm` : null)
    feature.acceptedBpmReviewNote = String(incoming.reviewNote || "").trim()
  }
  const pcgFinding = isCardiacCycleTimingFeature(feature)
    ? normalizeCardiacCycleEvidenceFinding(incoming.finding, feature.observed)
    : isHeartRateFeature(feature) && heartRateConfirmedInViewer(feature)
    ? heartRateSourceFinding(feature)
    : incoming.finding || "PCG Viewer evidence imported for Reviewing Clinician review."
  feature.sources.unshift({
    sourceId: "pcg_viewer",
    sourceLabel: "PCG Viewer",
    sourceType: "medaica",
    name: "PCG Viewer",
    finding: `${pcgFinding}${rhythmPatternNote}${structuralPatternNote}${supportingContextNote}${
      sourceParts.length ? " Source " + sourceParts.join(", ") + "." : ""
    }`,
    recordingId: incoming.sourceRecordingId || null,
    position: incoming.sourcePosition || null,
    reviewStatus: "unreviewed",
  })
  feature.markerPosition = calculateMarkerPosition(feature, currentHeartRateBpm(features))
  if (isHeartRateFeature(feature)) {
    // The accepted range, shown as two markers; a single accepted value
    // stays a single marker
    feature.sourceRangeBpm = finiteRange(incoming.rangeBpm) ? { ...incoming.rangeBpm } : null
    feature.clinicianRangeBpm = null
    feature.clinicianAssessmentMode = null
    feature.mixedRange = false
    const range = heartRateRangeOf(feature)
    if (range) {
      feature.clinicianRangeBpm = { ...range }
      feature.clinicianAssessmentMode = "range"
      const grades = heartRateRangeGrades(range, feature.normalRange, feature.scaleType)
      feature.status = grades.status
      feature.mixedRange = grades.mixed
      feature.aiStatus = feature.status === "green" ? "normal" : feature.status === "red" ? "abnormal" : "borderline"
      feature.markerPosition = heartRateRangeMarkerPosition(feature, range, currentHeartRateBpm(features))
    } else {
      feature.sourceRangeBpm = null
    }
  }
  // Patient-facing copy is product-controlled: it always follows the current
  // result and is never clinician-authored.
  setAutoPatientCopy(feature, feature.status)
  if ((feature.name === "Structural Sound" || feature.name === "Structural Sound Summary") && incoming.patientText) {
    feature.patientResultText = incoming.patientText
    feature.noteToPatient = composePatientNote(feature)
  }
  feature.noteToPatientEdited = false
  feature.noteToPatientStale = false
  // Always a boolean, so that edits that are later undone leave no trace
  feature.noteToDoctorEdited = !!feature.noteToDoctorEdited
  if (!feature.noteToDoctorEdited) {
    feature.noteToDoctor =
      (feature.name === "Structural Sound" || feature.name === "Structural Sound Summary") && incoming.providerText
        ? incoming.providerText
        : getAutoProviderNote(
            feature.name,
            feature.status,
            feature.observed,
            displayReferenceRange(feature, currentHeartRateBpm(features)),
            currentHeartRateBpm(features)
          ) ||
          feature.noteToDoctor ||
          ""
    feature.noteToDoctorStale = false
  } else {
    feature.noteToDoctorStale = true
  }
  feature.importedBaseline = buildFeatureBaseline(feature)
  return true
}

export function buildSysDiaDurationEvidence(cycleTimingEvidence, heartRateEvidence) {
  const timing = parseSysDiaMsFromEvidence(cycleTimingEvidence)
  if (!Number.isFinite(timing.sysMs) && !Number.isFinite(timing.diaMs)) return null
  const hr = Number(heartRateEvidence?.valueBpm ?? parseLeadingNumber(heartRateEvidence?.observed))
  const range = expectedDiaRangeForHeartRate(hr)
  const sysText = Number.isFinite(timing.sysMs) ? `${Math.round(timing.sysMs)} ms` : "not available"
  const diaText = Number.isFinite(timing.diaMs) ? `${Math.round(timing.diaMs)} ms` : "not available"
  // Medians (timing engine) or means (usable cycles): an exam roll-up names
  // the statistic its recordings share, or null when they differ; a single
  // recording's package says it through its calculation method
  const statistic =
    cycleTimingEvidence && "durationStatistic" in cycleTimingEvidence
      ? cycleTimingEvidence.durationStatistic
      : durationStatisticOf(cycleTimingEvidence)
  const durationsLead = statistic
    ? `${statistic.charAt(0).toUpperCase()}${statistic.slice(1)} systolic duration`
    : "Systolic duration"
  return {
    observed: `SYS ${sysText} · DIA ${diaText}`,
    status: sysDiaDurationStatusFromValues(timing.sysMs, timing.diaMs, hr, cycleTimingEvidence?.status),
    normalRange: `${range.min} – ${range.max} ms`,
    displayReferenceRange: range.label,
    finding: `${durationsLead} ${sysText} and diastolic duration ${diaText} across accepted cycles${
      Number.isFinite(hr) ? `; observed heart rate ${Math.round(hr)} bpm` : ""
    }.`,
    sourcePosition: cycleTimingEvidence?.sourcePosition,
    sourceRecordingId: cycleTimingEvidence?.sourceRecordingId,
    sourceRecordingIds: cycleTimingEvidence?.sourceRecordingIds,
    sysMs: timing.sysMs,
    diaMs: timing.diaMs,
    heartRateBpm: Number.isFinite(hr) ? hr : null,
    // The durations summarize the same recordings as the ratio, so the card
    // carries the same exam roll-up review flag.
    reviewRequired: !!cycleTimingEvidence?.reviewRequired,
    reviewReason: cycleTimingEvidence?.reviewRequired ? cycleTimingEvidence.reviewReason || null : null,
  }
}

export function ensureSysDiaDurationFeature(report) {
  if (!report.features || findFeatureByName(report.features, "SYS:DIA Duration")) return
  const ratioFeature = findFeatureByName(report.features, "SYS:DIA Ratio")
  const insertionIndex = ratioFeature ? report.features.indexOf(ratioFeature) + 1 : Math.min(2, report.features.length)
  const feature = {
    name: "SYS:DIA Duration",
    normalRange: "500 – 700 ms",
    displayReferenceRange: "Expected systolic and diastolic durations vary with heart rate",
    observed: "SYS 320 ms · DIA 580 ms",
    status: "green",
    scaleType: "bidirectional",
    type: "duration",
    interpretation: "Systolic and diastolic phase durations across accepted recordings",
    isHidden: false,
    showInBasic: false,
    showInPremium: true,
    showNumericInPatient: false,
    aiStatus: "normal",
    noteToPatient: "",
    noteToDoctor: "",
    dataSource: "viewer",
    referenceSource: "PCG Viewer cardiac cycle timing guide",
    sources: [
      {
        sourceId: "pcg_viewer",
        sourceLabel: "PCG Viewer",
        sourceType: "medaica",
        name: "PCG Viewer",
        finding: "Mean systolic and diastolic durations imported for Reviewing Clinician review.",
        reviewStatus: "unreviewed",
      },
    ],
  }
  feature.markerPosition = calculateMarkerPosition(feature, currentHeartRateBpm(report.features))
  report.features.splice(insertionIndex, 0, feature)
}

export function dedupeFeatureSources(features) {
  ;(features || []).forEach((feature) => {
    if (!feature || !Array.isArray(feature.sources)) return
    const byFeed = new Map()
    feature.sources.forEach((source) => {
      const feedId = sourceIdForFeed(source)
      if (!byFeed.has(feedId)) {
        byFeed.set(feedId, { ...source, name: feedId === "pcg_viewer" ? "PCG Viewer" : source.name })
        return
      }
      const existing = byFeed.get(feedId)
      const findings = [existing.finding, source.finding].filter(Boolean)
      existing.finding = [...new Set(findings)].join(" ")
      existing.reviewStatus =
        existing.reviewStatus === "rejected" || source.reviewStatus === "rejected"
          ? "rejected"
          : existing.reviewStatus === "accepted" || source.reviewStatus === "accepted"
          ? "accepted"
          : "unreviewed"
      existing.recordingId = existing.recordingId || source.recordingId || null
      existing.position = existing.position || source.position || null
      if (!existing.detailEvidence && source.name && String(source.name).toLowerCase().includes("detail"))
        existing.detailEvidence = true
    })
    feature.sources = Array.from(byFeed.values())
  })
}

// A card with no result to word or place: left out for want of a measurement,
// or waiting for a manual assessment the clinician has not started
const hasNoResult = (feature) =>
  !!feature.measurementUnavailable ||
  (feature.reportInclusionStatus === INCLUSION_UNRESOLVED && !feature.manualAssessmentStarted)

export function normalizeCandidateReportStructure(report) {
  const features = report.features
  const hr = () => currentHeartRateBpm(features)
  const cardiacCycleTiming = findFeatureByName(features, "SYS:DIA Ratio")
  if (cardiacCycleTiming) {
    cardiacCycleTiming.name = "SYS:DIA Ratio"
    cardiacCycleTiming.referenceSource = "PCG Viewer cardiac cycle timing guide"
    cardiacCycleTiming.normalRange = "0.40 – 0.67"
    cardiacCycleTiming.displayReferenceRange = "Expected resting range: 1 : 1.5 to 1 : 2.5"
    cardiacCycleTiming.interpretation = "Systolic duration divided by diastolic duration across accepted recordings"
    cardiacCycleTiming.showNumericInPatient = false
    if (/^-?\d+(?:\.\d+)?$/.test(String(cardiacCycleTiming.observed || "").trim())) {
      const ratio = Number(cardiacCycleTiming.observed)
      if (Number.isFinite(ratio) && ratio > 0) cardiacCycleTiming.observed = formatSystoleDiastoleDisplay(ratio)
    }
    cardiacCycleTiming.status = cardiacCycleTimingColorClass(
      cardiacCycleTiming.observed,
      cardiacCycleTiming.status,
      hr()
    )
    ;(cardiacCycleTiming.sources || []).forEach((source) => {
      source.finding = normalizeCardiacCycleEvidenceFinding(source.finding, cardiacCycleTiming.observed)
    })
    if (!cardiacCycleTiming.noteToPatientEdited) setAutoPatientCopy(cardiacCycleTiming, cardiacCycleTiming.status)
    if (!cardiacCycleTiming.noteToDoctorEdited && !hasNoResult(cardiacCycleTiming))
      cardiacCycleTiming.noteToDoctor =
        getAutoProviderNote(
          cardiacCycleTiming.name,
          cardiacCycleTiming.status,
          cardiacCycleTiming.observed,
          cardiacCycleTiming.displayReferenceRange,
          hr()
        ) || cardiacCycleTiming.noteToDoctor
  }

  ensureSysDiaDurationFeature(report)

  const sysDiaDuration = findFeatureByName(features, "SYS:DIA Duration")
  if (sysDiaDuration) {
    sysDiaDuration.name = "SYS:DIA Duration"
    sysDiaDuration.referenceSource = "PCG Viewer cardiac cycle timing guide"
    sysDiaDuration.interpretation = "Systolic and diastolic phase durations across accepted recordings"
    sysDiaDuration.showNumericInPatient = false
    sysDiaDuration.status = sysDiaDurationColorClass(sysDiaDuration.observed, sysDiaDuration.status, hr())
    if (!sysDiaDuration.sources || !sysDiaDuration.sources.length) {
      sysDiaDuration.sources = [
        {
          sourceId: "pcg_viewer",
          sourceLabel: "PCG Viewer",
          sourceType: "medaica",
          name: "PCG Viewer",
          finding: "Mean systolic and diastolic durations imported for Reviewing Clinician review.",
          reviewStatus: "unreviewed",
        },
      ]
    }
    if (!sysDiaDuration.noteToPatientEdited) setAutoPatientCopy(sysDiaDuration, sysDiaDuration.status)
    if (!sysDiaDuration.noteToDoctorEdited && !hasNoResult(sysDiaDuration))
      sysDiaDuration.noteToDoctor =
        getAutoProviderNote(
          sysDiaDuration.name,
          sysDiaDuration.status,
          sysDiaDuration.observed,
          sysDiaDuration.displayReferenceRange || sysDiaDuration.normalRange,
          hr()
        ) || sysDiaDuration.noteToDoctor
    if (!hasNoResult(sysDiaDuration)) sysDiaDuration.markerPosition = calculateMarkerPosition(sysDiaDuration, hr())
  }

  const rhythmRegularity = findFeatureByName(features, "Cycle Regularity")
  if (rhythmRegularity) {
    rhythmRegularity.name = "Cycle Regularity"
    rhythmRegularity.scaleType = "regularity"
    rhythmRegularity.normalRange = ">= 85% regular"
    rhythmRegularity.displayReferenceRange = rhythmRegularityReferenceText()
    rhythmRegularity.interpretation = "PCG-derived S1-to-S1 cycle timing proxy across accepted recordings"
    rhythmRegularity.showNumericInPatient = false
    rhythmRegularity.status = rhythmRegularityColorClass(rhythmRegularity.observed, rhythmRegularity.status)
    if (!rhythmRegularity.sources || !rhythmRegularity.sources.length) {
      rhythmRegularity.sources = [
        {
          sourceId: "pcg_viewer",
          sourceLabel: "PCG Viewer",
          sourceType: "medaica",
          name: "PCG Viewer",
          finding: "PCG beat-to-beat timing proxy imported for Reviewing Clinician review.",
          reviewStatus: "unreviewed",
        },
      ]
    }
    if (!rhythmRegularity.noteToPatientEdited) setAutoPatientCopy(rhythmRegularity, rhythmRegularity.status)
    if (!rhythmRegularity.noteToDoctorEdited && !hasNoResult(rhythmRegularity))
      rhythmRegularity.noteToDoctor =
        getAutoProviderNote(
          rhythmRegularity.name,
          rhythmRegularity.status,
          rhythmRegularity.observed,
          rhythmRegularity.displayReferenceRange,
          hr()
        ) || rhythmRegularity.noteToDoctor
    if (!hasNoResult(rhythmRegularity)) {
      rhythmRegularity.markerPosition = calculateMarkerPosition(rhythmRegularity, hr())
    }
  }

  const structuralDetail = (features || []).find((feature) => feature.name === "Structural Sound Detail")
  const structuralSummary = findFeatureByName(features, "Structural Sound")
  if (structuralSummary) structuralSummary.name = "Structural Sound"
  if (structuralSummary && structuralDetail) {
    structuralSummary.structuralDetailEvidence = {
      observed: structuralDetail.observed,
      status: structuralDetail.status,
      interpretation: structuralDetail.interpretation,
      sources: structuralDetail.sources || [],
    }
    const existingSourceNames = new Set((structuralSummary.sources || []).map((source) => sourceIdForFeed(source)))
    ;(structuralDetail.sources || []).forEach((source) => {
      const detailSource = {
        ...source,
        name: source.name === "PCG Viewer" ? "PCG Viewer" : source.name,
        finding: source.finding || "Structural-sound detail evidence.",
      }
      const feedId = sourceIdForFeed(detailSource)
      if (!existingSourceNames.has(feedId)) {
        structuralSummary.sources = structuralSummary.sources || []
        structuralSummary.sources.push(detailSource)
        existingSourceNames.add(feedId)
      } else {
        const existing = (structuralSummary.sources || []).find((item) => sourceIdForFeed(item) === feedId)
        if (existing && detailSource.finding && !String(existing.finding || "").includes(detailSource.finding)) {
          existing.finding = [existing.finding, detailSource.finding].filter(Boolean).join(" ")
        }
      }
    })
  }
  removePlaceholderThirdPartySources(features)
  dedupeFeatureSources(features)
  report.features = (features || []).filter(
    (feature) => !["Timing Summary", "Structural Sound Detail", "S1/S2 Intensity"].includes(feature.name)
  )
  if (!structuralSummary) return
  structuralSummary.referenceSource = "PCG Viewer structural-sound guide layer"
}

// Pure body of applyPcgViewerHandoff: maps one handoff package into the report model.
// The demo's transport (fetch /handoff/pcg/latest, localStorage fallback,
// cardiacPanelLastPcgImportV1 persistence) is platform plumbing — not vendored.
export function applyHandoffPackage(
  report,
  pkg,
  { formatDateTime = (value) => new Date(value).toLocaleString() } = {}
) {
  if (!pkg || pkg.schema !== HANDOFF_SCHEMA) return { applied: 0, ok: false }
  const e = pkg.evidence || {}
  const context = pkg.context || {}
  let applied = 0
  const candidateTiming = pkg.candidateEvidence?.timing || null
  const candidateStructural = pkg.candidateEvidence?.structuralSound || null
  applied += applyPcgFeature(report, "Signal Quality", withSupportingContext(pkg, "Signal Quality", e.signalQuality))
    ? 1
    : 0
  const cycleTimingEvidence = e.cardiacCycleTiming || e.systolicDiastolicTiming
  // The evidence each timing card takes, and the state it arrives in: a card
  // whose evidence measured nothing is unassessed, whatever its domain's state
  const applyTimingDomain = (heartRate, cycleTiming, regularity, sources, includedIds, state, metaFor) => {
    const evidenceByCard = {
      "Heart Rate": withSupportingContext(pkg, "Heart Rate", heartRate),
      "SYS:DIA Ratio": cycleTiming,
      "SYS:DIA Duration": buildSysDiaDurationEvidence(cycleTiming, heartRate),
      "Cycle Regularity": regularity,
    }
    TIMING_DOMAIN_CARDS.forEach((name) => {
      applied += applyPcgFeature(report, name, evidenceByCard[name]) ? 1 : 0
    })
    applyTimingSourceRecordings(report, sources, includedIds)
    TIMING_DOMAIN_CARDS.forEach((name) => {
      if (importedEvidenceUnavailable(evidenceByCard[name])) setFeatureUnassessed(report, name, evidenceByCard[name])
      else markReportEvidenceState(findFeatureByName(report.features, name), state, metaFor(name))
    })
  }
  // Evidence accepted in the viewer arrives already in the report. Timing
  // recordings that disagree on the heart rate arrive pending: the complete
  // range is shown and the reviewer decides.
  const timingAccepted = Array.isArray(e.heartRate?.sourceRecordingIds) && e.heartRate.sourceRecordingIds.length > 0
  if (timingAccepted) {
    applied += applyPcgFeature(report, "Timing Summary", e.timingSummary) ? 1 : 0
    const timingNeedsReview = e.heartRate.reviewRequired === true
    const timingMeta = timingNeedsReview ? timingRangeMeta(e.heartRate) : null
    applyTimingDomain(
      e.heartRate,
      cycleTimingEvidence,
      e.rhythmRegularity,
      e.heartRate.sourceRecordings || cycleTimingEvidence?.sourceRecordings || e.rhythmRegularity?.sourceRecordings,
      new Set((e.heartRate.sourceRecordingIds || pkg.examRollup?.timing?.includedRecordingIds || []).map(String)),
      timingNeedsReview ? INCLUSION_PENDING : INCLUSION_ACCEPTED,
      (name) => {
        if (!timingMeta) return null
        return name === "Heart Rate" ? timingMeta : { timingRange: true, reviewReason: "" }
      }
    )
  } else if (candidateTiming) {
    // Nothing accepted for Timing, but usable timing on a recording accepted
    // for Structural Sound: the cards arrive pending with those values
    const meta = candidateMeta(candidateTiming, "timing")
    applyTimingDomain(
      candidateTiming.heartRate,
      candidateTiming.cardiacCycleTiming,
      candidateTiming.rhythmRegularity,
      candidateTiming.heartRate?.sourceRecordings || candidateTiming.recordings,
      new Set(),
      INCLUSION_PENDING,
      () => ({ ...meta })
    )
  } else {
    // No Timing evidence at all is not a clinician exclusion: the cards a
    // clinician can assess by hand stay available in clinical review
    const reasonByCard = {
      "Heart Rate": e.heartRate,
      "SYS:DIA Ratio": cycleTimingEvidence,
      "Cycle Regularity": e.rhythmRegularity,
    }
    TIMING_DOMAIN_CARDS.forEach((name) => setFeatureUnassessed(report, name, reasonByCard[name] || cycleTimingEvidence))
  }
  applied += applyPcgFeature(report, "S1/S2 Intensity", e.s1s2IntensityBalance) ? 1 : 0
  const structuralAccepted =
    pkg.examRollup?.domainAvailability?.structuralSoundAccepted === true ||
    (Array.isArray(e.structuralSoundSummary?.sourceRecordingIds) &&
      e.structuralSoundSummary.sourceRecordingIds.some(Boolean)) ||
    !!e.structuralSoundSummary?.sourceRecordingId
  if (structuralAccepted) {
    // Accepted evidence that could not be scored leaves the card unassessed
    applied += applyPcgFeature(report, "Structural Sound", e.structuralSoundSummary) ? 1 : 0
    if (!importedEvidenceUnavailable(e.structuralSoundSummary)) {
      markReportEvidenceState(findFeatureByName(report.features, "Structural Sound"), INCLUSION_ACCEPTED)
    }
  } else if (candidateStructural && !importedEvidenceUnavailable(candidateStructural.summary)) {
    applied += applyPcgFeature(report, "Structural Sound", candidateStructural.summary) ? 1 : 0
    const structural = findFeatureByName(report.features, "Structural Sound")
    if (structural) structural.structuralDetailEvidence = null
    markReportEvidenceState(structural, INCLUSION_PENDING, candidateMeta(candidateStructural, "structuralSound"))
  } else {
    setFeatureUnassessed(report, "Structural Sound", e.structuralSoundSummary)
  }
  reconcileReportInclusionStates(report.features)
  if (structuralAccepted && !importedEvidenceUnavailable(e.structuralSoundSummary) && e.structuralSoundDetail) {
    const structural = findFeatureByName(report.features, "Structural Sound")
    if (structural) {
      structural.structuralDetailEvidence = e.structuralSoundDetail
      structural.sources = (structural.sources || []).filter((src) => src.name !== "PCG Viewer Detail")
      structural.sources.push({
        name: "PCG Viewer Detail",
        finding:
          e.structuralSoundDetail.finding ||
          "Phase-level structural-sound detail imported as evidence under Structural Sound.",
        recordingId: e.structuralSoundDetail.sourceRecordingId || null,
        position: e.structuralSoundDetail.sourcePosition || null,
        reviewStatus: "unreviewed",
      })
      applied += 1
    }
  }

  const rec = pkg.recording || {}
  const examScope = pkg.examScope || pkg.exam || {}
  const importedRecordings = Array.isArray(pkg.recordings) && pkg.recordings.length ? pkg.recordings : [rec]
  const normalizedRecordings = importedRecordings.map((sourceRec, index) => {
    const recordingId =
      sourceRec.recordingId || (index === 0 ? rec.recordingId || "pcg_latest" : `pcg_imported_${index + 1}`)
    const coverageStatus = sourceRec.coverageStatus || examScope.coverageStatus || null
    const coverageNote = sourceRec.coverageNote || examScope.coverageNote || ""
    const sourceExamScope =
      sourceRec.examScope ||
      examScope.scope ||
      (importedRecordings.length === 1 ? "single_recording" : "multi_recording_exam")
    return {
      recordingId,
      examId: sourceRec.examId || pkg.examId || examScope.examId || null,
      deviceId: sourceRec.audioKind || sourceRec.deviceId || "PCG Viewer",
      position: sourceRec.position || sourceRec.site || null,
      recordedAt: sourceRec.recordedAt
        ? formatDateTime(sourceRec.recordedAt)
        : pkg.createdAt
        ? formatDateTime(pkg.createdAt)
        : "",
      url: sourceRec.url || "/",
      duration: sourceRec.durationSec ? `${Number(sourceRec.durationSec).toFixed(1)}s` : "",
      sourceName: sourceRec.name || sourceRec.source || rec.name || "latest recording",
      evidenceScope: sourceRec.evidenceScope || "recording_only",
      examScope: sourceExamScope,
      coverageStatus,
      coverageNote,
      acceptedForExamRollup: sourceRec.acceptedForExamRollup !== false,
      // What the recording was accepted for; the report lists its sources by these
      acceptedDomains: sourceRec.acceptedDomains || null,
      timingEligible: sourceRec.timingEligible === undefined ? null : !!sourceRec.timingEligible,
      structuralSoundEligible:
        sourceRec.structuralSoundEligible === undefined ? null : !!sourceRec.structuralSoundEligible,
      reviewStatus: sourceRec.reviewStatus || null,
      // Stored ACT / BeatAware results and the reviewer's plugin reviews,
      // shown as information in the cards' Evidence panels
      vendorEvidence: sourceRec.vendorEvidence || null,
      pluginReviews: sourceRec.pluginReviews || null,
    }
  })
  const importedIds = new Set(normalizedRecordings.map((item) => item.recordingId))
  report.recordings = normalizedRecordings.concat(
    (report.recordings || []).filter((r) => !importedIds.has(r.recordingId))
  )
  attachThirdPartySources(report)

  report.longitudinalContext = {
    ...(report.longitudinalContext || {}),
    ...(context.longitudinal || {}),
    priorExams: context.priorExams || [],
    priorQuestionnaires: context.priorQuestionnaires || [],
    source: context.schema || "ExamContextPackage",
  }
  if (context.currentQuestionnaire && context.currentQuestionnaire.sections) {
    report.questionnaire = context.currentQuestionnaire
  }
  const hcpReview = pkg.hcpReview || {}
  const importedRecordingNotes = Array.isArray(hcpReview.recordingNotes)
    ? hcpReview.recordingNotes
    : Array.isArray(context.recordingNotes)
    ? context.recordingNotes
    : []
  const existingRecordingNotes = Array.isArray(report.recordingNotes) ? report.recordingNotes : []
  const recordingNoteKey = (note) =>
    [(note && note.examId) || "", (note && note.recordingId) || "", (note && note.recordingSequence) || ""].join(":")
  const existingRecordingNotesByKey = new Map(existingRecordingNotes.map((note) => [recordingNoteKey(note), note]))
  report.recordingNotes = importedRecordingNotes
    .filter(
      (note) =>
        note &&
        (String(note.text || "").trim() ||
          String(note.reportNote || "").trim() ||
          note.acousticQualityLabel ||
          note.hcpConfirmedBpm ||
          note.acousticFindingLabel ||
          note.dominantPatternLabel ||
          (Array.isArray(note.otherAcousticObservationLabels) && note.otherAcousticObservationLabels.length) ||
          (Array.isArray(note.rhythmObservationLabels) && note.rhythmObservationLabels.length))
    )
    .map((note) => {
      const existing = existingRecordingNotesByKey.get(recordingNoteKey(note))
      const viewerReportNote = String(note.reportNote || "").trim()
      // Upstream (cardiac-panel-v4 eba1cd1): Report wording the reviewer
      // edited in the report — a Report note, or the report wording of a note
      // from before the split — becomes the Report note when the viewer
      // supplies none.
      const previouslyEditedReportNote = existing?.reportEditedAt
        ? String(existing.reportNote || existing.reportText || "").trim()
        : ""
      // An in-review report rebases on open whenever the review changed, so
      // a Report note edited here also survives while the viewer's Report note
      // is the one it was edited from (upstream lets the viewer's win, and
      // drops the edit's timestamp, so an edit lasts one re-import).
      const keepsEdit = Boolean(existing?.reportEditedAt) && existing.viewerReportNote === viewerReportNote
      const reportNote = keepsEdit ? String(existing.reportNote || "") : viewerReportNote || previouslyEditedReportNote
      const edited = keepsEdit || (!viewerReportNote && Boolean(previouslyEditedReportNote))
      const unchangedSource =
        existing &&
        recordingNoteObservationText(existing) === recordingNoteObservationText(note) &&
        (existing.viewerReportNote ?? "") === viewerReportNote
      return {
        ...note,
        reportNote,
        viewerReportNote,
        ...(edited ? { reportEditedAt: existing.reportEditedAt } : {}),
        visibility: "internal_review",
        patientAccessExpected: false,
        reportNoteVisibility: reportNote.trim() ? "patient_and_care_team" : null,
        reviewStatus: unchangedSource ? existing.reviewStatus || "recorded" : note.reviewStatus || "recorded",
      }
    })
  report.recordingNoteInstruction = hcpReview.recordingNoteInstruction || DEFAULT_RECORDING_NOTE_INSTRUCTION
  // Exam roll-up audit block (which recordings fed the summary and why)
  report.examRollup = pkg.examRollup ? JSON.parse(JSON.stringify(pkg.examRollup)) : null

  const screening = pkg.screening || {}
  report.summary = report.summary || {}
  report.summary = {
    ...report.summary,
    ...(screening.summary || {}),
    patientText: PATIENT_SUMMARY_TEXT,
    providerText: providerSummaryText(
      (screening.summary && screening.summary.providerText) || "",
      report.features,
      currentHeartRateBpm(report.features)
    ),
  }
  report.followUpNote = report.followUpNote || {}
  const importedProviderFollowUp =
    screening.followUpNote && (screening.followUpNote.hcpText || screening.followUpNote.providerText)
  report.followUpNote = {
    ...report.followUpNote,
    ...(screening.followUpNote || {}),
    providerText: sanitizeProviderFollowUpText(
      importedProviderFollowUp ||
        "This screening report is intended to support clinician assessment, not replace it. " +
          "Interpret alongside relevant history, symptoms, physical examination, and standard " +
          "clinical testing when clinically indicated."
    ),
    patientText:
      screening.followUpNote && (screening.followUpNote.consumerText || screening.followUpNote.patientText)
        ? screening.followUpNote.consumerText || screening.followUpNote.patientText
        : report.followUpNote.patientText,
  }

  const importStatus = {
    createdAt: pkg.createdAt,
    recordingName: normalizedRecordings[0]?.sourceName || rec.name || "latest recording",
    recordingCount: normalizedRecordings.length,
    applied,
    sourceApp: pkg.app || null,
    heartRate: e.heartRate && e.heartRate.observed,
    timing: e.timingSummary && e.timingSummary.observed,
    structural: e.structuralSoundSummary && e.structuralSoundSummary.observed,
  }
  return { applied, ok: applied > 0, importStatus }
}
